import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { sessionsAPI, pprwAPI, partnerConferencesAPI, regionalInPersonAPI } from '../../services/api';

const SOURCES = [
  { key: 'private_delivery_session', label: 'Private Delivery Session', titleKey: 'title', secondaryKey: 'course_title', dateKey: 'session_date', statusKeys: ['status'],
    fetch: () => sessionsAPI.getAll(), update: (item, cid, ctitle) => sessionsAPI.update(item.id, { content_item_id: cid, content_item_title: ctitle }) },
  { key: 'pprw', label: 'PPRW (1:Many)', titleKey: 'course_title', dateKey: 'start_date', statusKeys: ['delivery_status'],
    fetch: () => pprwAPI.getAll(), update: (item, cid, ctitle) => pprwAPI.update(item.id, { content_item_id: cid, content_item_title: ctitle }) },
  { key: 'partner_conferences', label: 'Partner Conference', titleKey: 'title', dateKey: 'preferred_dates', statusKeys: ['delivery_status', 'status'],
    fetch: () => partnerConferencesAPI.getAll(), update: (item, cid, ctitle) => partnerConferencesAPI.update(item.id, { content_item_id: cid, content_item_title: ctitle }) },
  { key: 'regional_in_person', label: 'Regional In-Person', titleKey: 'title', dateKey: 'preferred_dates', statusKeys: ['delivery_status', 'status'],
    fetch: () => regionalInPersonAPI.getAll(), update: (item, cid, ctitle) => regionalInPersonAPI.update(item.id, { content_item_id: cid, content_item_title: ctitle }) },
];

const getStatus = (item, source) => {
  for (const k of (source.statusKeys || [])) {
    if (item[k]) return String(item[k]);
  }
  return '';
};
const isCancelled = (item, source) => /cancel/i.test(getStatus(item, source));
const isPast = (item, source) => {
  const raw = item[source.dateKey];
  if (!raw || typeof raw !== 'string') return false;
  const m = raw.match(/\d{4}-\d{2}-\d{2}/);
  if (!m) return false; // unparseable (e.g. free-text date range) — don't treat as past
  const today = new Date().toISOString().slice(0, 10);
  return m[0] < today;
};

export const LinkExistingDeliveryDialog = ({ open, onOpenChange, contentItemId, contentItemTitle, onLinked }) => {
  const [sourceKey, setSourceKey] = useState('private_delivery_session');
  const [search, setSearch] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [linkingId, setLinkingId] = useState(null);
  const [showPastCancelled, setShowPastCancelled] = useState(false);

  const source = SOURCES.find(s => s.key === sourceKey);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    source.fetch().then(setItems).catch(() => setItems([])).finally(() => setLoading(false));
  }, [open, sourceKey]);

  const hiddenCount = items.filter(i => isCancelled(i, source) || isPast(i, source)).length;

  const filtered = items.filter(i => {
    const haystack = `${i[source.titleKey] || ''} ${source.secondaryKey ? (i[source.secondaryKey] || '') : ''}`.toLowerCase();
    if (!haystack.includes(search.toLowerCase())) return false;
    if (!showPastCancelled && (isCancelled(i, source) || isPast(i, source))) return false;
    return true;
  });

  const handleLink = async (item) => {
    setLinkingId(item.id);
    try {
      await source.update(item, contentItemId, contentItemTitle);
      toast.success('Delivery linked to this course');
      onLinked();
      onOpenChange(false);
    } catch {
      toast.error('Failed to link delivery');
    } finally {
      setLinkingId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg overflow-hidden" data-testid="link-existing-delivery-dialog">
        <DialogHeader>
          <DialogTitle>Link an Existing Delivery</DialogTitle>
          <DialogDescription>Search a scheduled delivery and link it to this course.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Select value={sourceKey} onValueChange={(v) => { setSourceKey(v); setSearch(''); }}>
            <SelectTrigger data-testid="link-delivery-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {SOURCES.map(s => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input placeholder="Search by title..." value={search} onChange={e => setSearch(e.target.value)} data-testid="link-delivery-search-input" />
          <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer" data-testid="show-past-cancelled-label">
            <Checkbox checked={showPastCancelled} onCheckedChange={setShowPastCancelled} data-testid="show-past-cancelled-checkbox" />
            Show past &amp; cancelled deliveries
            {!showPastCancelled && hiddenCount > 0 && <span className="text-amber-600">({hiddenCount} hidden)</span>}
          </label>
          <div className="max-h-72 overflow-y-auto overflow-x-hidden space-y-1.5 pr-1 -mr-1">
            {loading ? (
              <p className="text-sm text-muted-foreground py-6 text-center">Loading...</p>
            ) : filtered.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center">No upcoming deliveries found.</p>
            ) : filtered.slice(0, 50).map(item => {
              const cancelled = isCancelled(item, source);
              const past = isPast(item, source);
              return (
              <div
                key={item.id}
                role="button"
                tabIndex={0}
                onClick={() => { if (linkingId !== item.id) handleLink(item); }}
                onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && linkingId !== item.id) { e.preventDefault(); handleLink(item); } }}
                className={`flex items-center justify-between gap-2 border rounded-lg px-3 py-2 min-w-0 cursor-pointer hover:border-lime hover:bg-lime/5 transition-colors ${linkingId === item.id ? 'opacity-60 pointer-events-none' : ''}`}
                data-testid={`link-delivery-option-${item.id}`}
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">
                    {source.secondaryKey ? (item[source.secondaryKey] || item[source.titleKey] || 'Untitled') : (item[source.titleKey] || 'Untitled')}
                  </p>
                  <p className="text-xs text-muted-foreground truncate flex items-center gap-1.5 flex-wrap">
                    <span>{item[source.dateKey] || 'No date set'}</span>
                    {cancelled && <Badge variant="outline" className="text-[10px] bg-red-50 text-red-600 border-red-200">Cancelled</Badge>}
                    {past && !cancelled && <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">Past</Badge>}
                    {source.secondaryKey && item[source.secondaryKey] && item[source.titleKey] && item[source.secondaryKey] !== item[source.titleKey] ? <span>· {item[source.titleKey]}</span> : null}
                    {item.content_item_id && item.content_item_id !== contentItemId ? <span>· already linked to another course</span> : null}
                  </p>
                </div>
                <Button size="sm" variant="outline" disabled={linkingId === item.id} onClick={(e) => { e.stopPropagation(); handleLink(item); }} className="flex-shrink-0" data-testid={`link-delivery-confirm-${item.id}`}>
                  {linkingId === item.id ? 'Linking...' : 'Link'}
                </Button>
              </div>
              );
            })}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LinkExistingDeliveryDialog;
